/**
 * Sidebar 2-level tab component (Phase 3).
 *
 * Renders a WAI-ARIA-compliant tablist at the top of the sidebar with tabs
 * for Tone, Region, and Template Stack, then shows the selected panel below.
 *
 * Keyboard: Left/Right arrow keys move focus between tabs per the WAI-ARIA
 * tablist pattern. The active tab is driven by `store.get().uiActiveTab` and
 * changes via `store.patch({ uiActiveTab })`.
 */

import type { Store } from '../state';

// ── Minimal i18n surface ─────────────────────────────────────────────────────

export interface SidebarTabsI18n {
  t(key: string, params?: Record<string, unknown>): string;
  onLocaleChange(cb: () => void): void;
}

// ── Public handle ────────────────────────────────────────────────────────────

export interface SidebarTabsHandles {
  /** Programmatically switch the active tab without going through store.patch. */
  setActiveTab(tab: 'region' | 'tone' | 'template'): void;
  destroy(): void;
}

// ── Factory ──────────────────────────────────────────────────────────────────

/**
 * Create and mount the two-tab sidebar shell.
 *
 * The component appends itself to `parent`. It wraps `regionPanel` and
 * `tonePanel` and shows exactly one at a time, controlled by
 * `store.get().uiActiveTab`.
 */
export function createSidebarTabs(opts: {
  parent: HTMLElement;
  i18n: SidebarTabsI18n;
  store: Store;
  regionPanel: HTMLElement;
  tonePanel: HTMLElement;
  templatePanel: HTMLElement;
}): SidebarTabsHandles {
  const { parent, i18n, store, regionPanel, tonePanel, templatePanel } = opts;

  // ── Tablist container ────────────────────────────────────────────────────
  const tablist = document.createElement('div');
  tablist.className = 'sidebar-tabs';
  tablist.setAttribute('role', 'tablist');
  tablist.setAttribute('aria-label', i18n.t('aria.regionList.label'));

  // ── Tab buttons ──────────────────────────────────────────────────────────
  type SidebarTab = 'region' | 'tone' | 'template';

  function makeTab(tab: SidebarTab): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sidebar-tab';
    btn.setAttribute('role', 'tab');
    btn.dataset['tab'] = tab;

    const icon = document.createElement('span');
    icon.className = 'sidebar-tab-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = tab === 'region' ? '◧' : tab === 'tone' ? '◯' : '▤';

    const label = document.createElement('span');
    label.className = 'sidebar-tab-label';
    label.textContent = i18n.t(`tab.${tab}`);

    btn.appendChild(icon);
    btn.appendChild(label);

    i18n.onLocaleChange(() => {
      label.textContent = i18n.t(`tab.${tab}`);
      btn.setAttribute('aria-label', i18n.t(`aria.tab.${tab}`));
      btn.title = i18n.t(`aria.tab.${tab}`);
    });

    btn.setAttribute('aria-label', i18n.t(`aria.tab.${tab}`));
    btn.title = i18n.t(`aria.tab.${tab}`);

    return btn;
  }

  // Tone tab is rendered first (left) per Item 1.
  const toneTabBtn = makeTab('tone');
  const regionTabBtn = makeTab('region');
  const templateTabBtn = makeTab('template');

  tablist.appendChild(toneTabBtn);
  tablist.appendChild(regionTabBtn);
  tablist.appendChild(templateTabBtn);

  // ── Panel container ──────────────────────────────────────────────────────
  const panelContainer = document.createElement('div');
  panelContainer.className = 'sidebar-tab-panels';

  parent.appendChild(tablist);
  parent.appendChild(panelContainer);

  // ── Sync visual state to store ───────────────────────────────────────────
  function syncToTab(tab: SidebarTab): void {
    const isTone = tab === 'tone';
    const isRegion = tab === 'region';
    const isTemplate = tab === 'template';

    toneTabBtn.setAttribute('aria-selected', String(isTone));
    regionTabBtn.setAttribute('aria-selected', String(isRegion));
    templateTabBtn.setAttribute('aria-selected', String(isTemplate));
    toneTabBtn.tabIndex = isTone ? 0 : -1;
    regionTabBtn.tabIndex = isRegion ? 0 : -1;
    templateTabBtn.tabIndex = isTemplate ? 0 : -1;

    // Move the correct panel into the container.
    panelContainer.replaceChildren(
      isTone ? tonePanel : isRegion ? regionPanel : templatePanel,
    );
  }

  // Initial sync.
  syncToTab(store.get().uiActiveTab);

  // Subscribe to store changes that flip the active tab.
  store.subscribe((state, patch) => {
    if ('uiActiveTab' in patch) {
      syncToTab(state.uiActiveTab);
    }
  });

  // ── Click handlers ───────────────────────────────────────────────────────
  regionTabBtn.addEventListener('click', () => {
    store.patch({ uiActiveTab: 'region' });
  });

  toneTabBtn.addEventListener('click', () => {
    store.patch({ uiActiveTab: 'tone' });
  });

  templateTabBtn.addEventListener('click', () => {
    store.patch({ uiActiveTab: 'template' });
  });

  // ── Keyboard: Left/Right arrow keys move focus (WAI-ARIA tablist) ────────
  tablist.addEventListener('keydown', (event: KeyboardEvent) => {
    // DOM order: [tone (0), region (1), template (2)]
    const tabs = [toneTabBtn, regionTabBtn, templateTabBtn];
    const focusedIndex = tabs.findIndex((t) => document.activeElement === t);
    if (focusedIndex === -1) return;

    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIndex = (focusedIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (focusedIndex - 1 + tabs.length) % tabs.length;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      tabs[nextIndex]?.focus();
      // Per WAI-ARIA: focusing a tab activates it.
      const nextTab = nextIndex === 0 ? 'tone' : nextIndex === 1 ? 'region' : 'template';
      store.patch({ uiActiveTab: nextTab });
    }
  });

  // ── Handles ──────────────────────────────────────────────────────────────
  return {
    setActiveTab(tab: 'region' | 'tone'): void {
      syncToTab(tab);
    },
    destroy(): void {
      tablist.remove();
      panelContainer.remove();
    },
  };
}
